import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion } from 'framer-motion';
import { TrendingUp, Award, Target, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardStatsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const statsUrl = token ? `${BACKEND_URL}/api/stats/me` : `${BACKEND_URL}/api/stats`;
        const [statsRes, achRes] = await Promise.all([
          fetch(statsUrl, { headers }),
          fetch(`${BACKEND_URL}/api/achievements`)
        ]);
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        } else {
          // fallback to global stats
          const globalRes = await fetch(`${BACKEND_URL}/api/stats`);
          setStats(await globalRes.json());
        }
        const achData = await achRes.json();
        setAchievements(achData);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const exportPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    doc.setFontSize(24);
    doc.text('PANTASS - Progress Report', 20, 25);
    doc.setFontSize(12);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 35);

    if (stats) {
      doc.setFontSize(16);
      doc.text('Overall Statistics', 20, 50);
      doc.setFontSize(12);
      doc.text(`Total Matches: ${stats.total_matches}`, 20, 62);
      doc.text(`Total Practice Sessions: ${stats.total_practice}`, 20, 72);
      doc.text(`Average Accuracy: ${stats.avg_accuracy}%`, 20, 82);
      doc.text(`Best Streak: ${stats.best_streak}`, 20, 92);
      doc.text(`Total Correct Answers: ${stats.total_correct}`, 20, 102);
      doc.text(`Total Wrong Answers: ${stats.total_wrong}`, 20, 112);
    }

    if (achievements.length > 0) {
      doc.setFontSize(16);
      doc.text('Achievements', 20, 130);
      doc.setFontSize(12);
      achievements.forEach((ach, i) => {
        doc.text(`${ach.unlocked ? '\u2713' : '\u2717'} ${ach.title} - ${ach.description}`, 20, 142 + i * 10);
      });
    }

    doc.save('PANTASS_Progress_Report.pdf');
  };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <h1 className="dashboard-header-title">{t('progressDashboard', language)}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        {loading ? (
          <div className="lb-loading"><div className="lb-spinner" /></div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            {/* Stats Cards */}
            {stats && (
              <div className="stats-grid">
                <div className="stat-card stat-card-blue">
                  <TrendingUp size={24} />
                  <div className="stat-card-value">{stats.total_matches}</div>
                  <div className="stat-card-label">{t('totalMatches', language)}</div>
                </div>
                <div className="stat-card stat-card-green">
                  <Target size={24} />
                  <div className="stat-card-value">{stats.avg_accuracy}%</div>
                  <div className="stat-card-label">{t('avgAccuracy', language)}</div>
                </div>
                <div className="stat-card stat-card-orange">
                  <Zap size={24} />
                  <div className="stat-card-value">{stats.best_streak}</div>
                  <div className="stat-card-label">{t('bestStreakLabel', language)}</div>
                </div>
                <div className="stat-card stat-card-purple">
                  <Award size={24} />
                  <div className="stat-card-value">{stats.total_correct}</div>
                  <div className="stat-card-label">{t('totalCorrect', language)}</div>
                </div>
              </div>
            )}

            {/* Achievements */}
            <h3 className="achievements-title">{t('achievementsTitle', language)}</h3>
            <div className="achievements-grid">
              {achievements.map((ach, i) => (
                <div key={i} className={`achievement-card ${ach.unlocked ? 'achievement-unlocked' : 'achievement-locked'}`}>
                  <div className="achievement-icon">{ach.icon}</div>
                  <div className="achievement-info">
                    <h4 className="achievement-name">{language === 'bm' ? ach.title_bm : ach.title}</h4>
                    <p className="achievement-desc">{language === 'bm' ? ach.description_bm : ach.description}</p>
                  </div>
                  {ach.unlocked && <div className="achievement-check">✓</div>}
                </div>
              ))}
            </div>

            {/* Export Button */}
            <button className="export-pdf-btn" onClick={exportPDF} data-testid="export-pdf-button">
              {t('exportPDF', language)}
            </button>

            <button className="setup-back-btn" onClick={() => navigate('/')}>&larr; {t('backToHome', language)}</button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
